import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { Flag, Plus, Tag, Link2 } from "lucide-react";
import { CardHeader, EmptyState, GlassCard, T, TYPE, formatDate, primaryButtonSx, ghostButtonSx, labelSx } from "../../user/complaints/shared";
import { pm, errorMessage } from "../api";
import { usePm } from "../PmContext";
import { Progress, has } from "../ui";
import { MultiSelect, PersonPicker, Select, dateInput, fieldSx } from "../inputs";

const DAY = 86400000;
// issue dates are whole days stored at UTC midnight (what <input type="date"> sends)
const dayOf = (d) => Math.floor(new Date(d).getTime() / DAY);
const dayIso = (n) => new Date(n * DAY).toISOString().slice(0, 10);
const startOf = (d, zoom) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  if (zoom === "month" || zoom === "quarter") x.setDate(1);
  return x;
};

const MilestoneDialog = ({ state, project, epics, versions, onClose, onSaved }) => {
  const ctx = usePm();
  const [form, setForm] = useState({});
  useEffect(() => {
    if (state) setForm({ name: state.name || "", description: state.description || "", dueDate: dateInput(state.dueDate), ownerId: state.owner?.id ? String(state.owner.id) : null, status: state.status || "open", epicIds: (state.epicIds || []).map(String), versionIds: (state.versionIds || []).map(String) });
  }, [state]);
  if (!state) return null;
  const save = async () => {
    try {
      const body = { ...form, dueDate: form.dueDate || null };
      if (state._id) await pm.updateMilestone(state._id, body);
      else await pm.createMilestone(project._id, body);
      onSaved();
    } catch (err) {
      ctx.fail(err);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{state._id ? "Edit milestone" : "New milestone"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField size="small" label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={fieldSx} />
          <TextField size="small" multiline minRows={2} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} sx={fieldSx} />
          <Stack direction="row" spacing={2}>
            <TextField fullWidth size="small" type="date" label="Due" InputLabelProps={{ shrink: true }} value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} sx={fieldSx} />
            <Select label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={["open", "done", "missed"].map((v) => ({ value: v, label: v }))} />
          </Stack>
          <PersonPicker label="Owner" value={form.ownerId} onChange={(v) => setForm({ ...form, ownerId: v })} />
          <MultiSelect label="Epics" value={form.epicIds} onChange={(v) => setForm({ ...form, epicIds: v })} options={epics.map((e) => ({ value: String(e._id), label: `${e.key} ${e.summary}` }))} />
          <MultiSelect label="Releases" value={form.versionIds} onChange={(v) => setForm({ ...form, versionIds: v })} options={versions.map((v) => ({ value: String(v._id), label: v.name }))} />
        </Stack>
      </DialogContent>
      <DialogActions>
        {state._id && <Button color="error" sx={{ mr: "auto" }} onClick={async () => { if (window.confirm("Delete milestone?")) { await pm.deleteMilestone(state._id); onSaved(); } }}>Delete</Button>}
        <Button variant="outlined" sx={ghostButtonSx} onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} disabled={!form.name} onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const DatesDialog = ({ epic, onClose, onSaved }) => {
  const ctx = usePm();
  const [start, setStart] = useState("");
  const [due, setDue] = useState("");
  useEffect(() => {
    if (epic) {
      setStart(dateInput(epic.startDate));
      setDue(dateInput(epic.dueDate));
    }
  }, [epic]);
  if (!epic) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{epic.key} dates</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={start} onChange={(e) => setStart(e.target.value)} sx={fieldSx} />
          <TextField size="small" type="date" label="Due" InputLabelProps={{ shrink: true }} value={due} onChange={(e) => setDue(e.target.value)} sx={fieldSx} />
          <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Without dates, the bar spans the dates of the issues in the epic.</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} onClick={async () => { try { await pm.updateIssue(epic._id, { startDate: start || null, dueDate: due || null }); onSaved(); } catch (err) { ctx.fail(err); } }}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const Roadmap = ({ project, version }) => {
  const ctx = usePm();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState("month");
  const [msDialog, setMsDialog] = useState(null);
  const [datesFor, setDatesFor] = useState(null);
  const [tick, setTick] = useState(0);
  const barRefs = useRef({});
  const [lines, setLines] = useState([]);
  const gridRef = useRef(null);
  // bar drag / resize: live preview while dragging, kept until the reload after saving
  const [preview, setPreview] = useState(null);
  const lastDragEnd = useRef(0);
  useEffect(() => {
    pm.roadmap(project._id).then((d) => { setData(d); setPreview(null); }).catch((err) => setError(errorMessage(err)));
  }, [project._id, version, tick]);

  const range = useMemo(() => {
    if (!data) return null;
    const dates = [
      ...data.epics.flatMap((e) => [e.barStart, e.barEnd]), ...data.milestones.map((m) => m.dueDate), ...data.versions.flatMap((v) => [v.startDate, v.releaseDate]),
      project.startDate, project.endDate, new Date(),
    ].filter(Boolean).map((d) => new Date(d).getTime());
    const min = startOf(Math.min(...dates) - 7 * DAY, zoom);
    const maxRaw = new Date(Math.max(...dates) + 21 * DAY);
    const max = new Date(maxRaw.getFullYear(), maxRaw.getMonth() + 1, 1);
    return { min, max, span: max - min };
  }, [data, zoom, project.startDate, project.endDate]);

  const pxPerDay = { week: 28, month: 9, quarter: 3.2 }[zoom];
  const width = range ? Math.max(700, (range.span / DAY) * pxPerDay) : 700;
  const x = (d) => (range ? ((new Date(d) - range.min) / DAY) * pxPerDay : 0);

  // dependency lines between bars (measured after layout)
  useEffect(() => {
    if (!data || !gridRef.current) return;
    const box = gridRef.current.getBoundingClientRect();
    const out = [];
    for (const d of data.dependencies) {
      const a = barRefs.current[String(d.from)];
      const b = barRefs.current[String(d.to)];
      if (!a || !b) continue;
      const ra = a.getBoundingClientRect();
      const rb = b.getBoundingClientRect();
      out.push({ x1: ra.right - box.left, y1: ra.top + ra.height / 2 - box.top, x2: rb.left - box.left, y2: rb.top + rb.height / 2 - box.top, label: d.label });
    }
    setLines(out);
  }, [data, zoom, width, preview]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data || !range) return <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box>;
  const canManage = has(project, "roadmap.manage") && !project.archivedAt;
  const canDrag = has(project, "issue.update") && !project.archivedAt;

  const saveDates = async (epic, start, end) => {
    try {
      await pm.updateIssue(epic._id, { startDate: dayIso(start), dueDate: dayIso(end), ...(epic.rev !== undefined ? { rev: epic.rev } : {}) });
      ctx.notify(`${epic.key}: ${formatDate(dayIso(start))} → ${formatDate(dayIso(end))}`);
    } catch (err) {
      if (err?.response?.status === 409) ctx.notify(`${epic.key} was changed by someone else — reloaded the latest dates`, "warning");
      else ctx.fail(err, "Could not change the dates");
    }
    setTick((t) => t + 1);
  };

  /** mode: move (both dates) | start | end. Snaps to whole days at the current zoom. */
  const startDrag = (ev, epic, mode) => {
    if (!canDrag || ev.button !== 0) return;
    ev.stopPropagation();
    ev.preventDefault();
    const s0 = dayOf(epic.barStart || epic.barEnd);
    const e0 = dayOf(epic.barEnd || epic.barStart);
    const st = { x0: ev.clientX, start: s0, end: e0, moved: false };
    const onMove = (m) => {
      const dx = m.clientX - st.x0;
      if (!st.moved && Math.abs(dx) < 4) return;
      st.moved = true;
      const dd = Math.round(dx / pxPerDay);
      if (mode === "move") Object.assign(st, { start: s0 + dd, end: e0 + dd });
      else if (mode === "start") st.start = Math.min(s0 + dd, e0);
      else st.end = Math.max(e0 + dd, s0);
      setPreview((p) => (p && p.id === String(epic._id) && p.start === st.start && p.end === st.end ? p : { id: String(epic._id), start: st.start, end: st.end }));
    };
    const stop = (cancelled) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      if (!st.moved) return;
      lastDragEnd.current = Date.now();
      if (cancelled || (st.start === s0 && st.end === e0)) setPreview(null);
      else saveDates(epic, st.start, st.end);
    };
    const onUp = () => stop(false);
    const onCancel = () => stop(true);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
  };
  const onBarClick = (e) => {
    if (Date.now() - lastDragEnd.current < 300) return; // the click that ends a drag
    if (canManage) setDatesFor(e);
    else navigate(`/projects/${project.key}/issues/${e.key}`);
  };
  const handleSx = (side) => ({ position: "absolute", top: 0, bottom: 0, [side]: 0, width: 7, cursor: "ew-resize", zIndex: 1, "&:hover": { bgcolor: "rgba(255,255,255,.35)" } });

  const months = [];
  for (let d = new Date(range.min); d < range.max; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) months.push(new Date(d));
  const ROW = 38;

  return (
    <Box>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup size="small" exclusive value={zoom} onChange={(_, v) => v && setZoom(v)}>
          {["week", "month", "quarter"].map((z) => <ToggleButton key={z} value={z} sx={{ textTransform: "none", fontSize: TYPE.micro }}>{z}</ToggleButton>)}
        </ToggleButtonGroup>
        <Stack direction="row" spacing={1.5} sx={{ ml: 1 }}>
          <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 14, height: 8, borderRadius: 2, bgcolor: "#7A5AF8" }} /><Typography sx={{ fontSize: TYPE.micro }}>Epic</Typography></Stack>
          <Stack direction="row" spacing={0.5} alignItems="center"><Flag size={12} color="#F79009" /><Typography sx={{ fontSize: TYPE.micro }}>Milestone</Typography></Stack>
          <Stack direction="row" spacing={0.5} alignItems="center"><Tag size={12} color="#12B76A" /><Typography sx={{ fontSize: TYPE.micro }}>Release</Typography></Stack>
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        {canManage && <Button size="small" variant="contained" sx={primaryButtonSx} startIcon={<Plus size={14} />} onClick={() => setMsDialog({})}>Milestone</Button>}
      </Stack>
      {!data.epics.length && !data.milestones.length && !data.versions.length ? (
        <GlassCard><EmptyState icon={Flag} title="Nothing on the roadmap yet" description="Create epics (with dates), releases and milestones — they appear here on a timeline." /></GlassCard>
      ) : (
        <GlassCard sx={{ overflow: "hidden" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "260px minmax(0,1fr)" }}>
            <Box sx={{ borderRight: `1px solid ${T.border}` }}>
              <Box sx={{ height: 40, borderBottom: `1px solid ${T.border}`, px: 1.5, display: "flex", alignItems: "center" }}><Typography sx={labelSx}>Epic</Typography></Box>
              {data.epics.map((e) => (
                <Box key={e._id} sx={{ height: ROW, px: 1.5, display: "flex", alignItems: "center", gap: 1, borderBottom: `1px solid ${T.border}`, cursor: "pointer", "&:hover": { bgcolor: T.surfaceMuted } }} onClick={() => navigate(`/projects/${project.key}/issues/${e.key}`)}>
                  <Typography noWrap sx={{ fontSize: TYPE.small, flexGrow: 1 }}><b>{e.key}</b> {e.summary}</Typography>
                  <Box sx={{ width: 50 }}><Progress value={e.progressPct} height={4} /></Box>
                </Box>
              ))}
              <Box sx={{ height: ROW, px: 1.5, display: "flex", alignItems: "center" }}><Typography sx={{ ...labelSx, fontSize: "0.66rem" }}>Milestones & releases</Typography></Box>
            </Box>
            <Box sx={{ overflowX: "auto" }}>
              <Box ref={gridRef} sx={{ width, position: "relative" }}>
                <Box sx={{ height: 40, borderBottom: `1px solid ${T.border}`, position: "relative" }}>
                  {months.map((m) => (
                    <Box key={m.toISOString()} sx={{ position: "absolute", left: x(m), top: 0, bottom: 0, borderLeft: `1px solid ${T.border}`, pl: 0.75, display: "flex", alignItems: "center" }}>
                      <Typography sx={{ fontSize: TYPE.micro, fontWeight: 600, color: T.inkSecondary, whiteSpace: "nowrap" }}>{m.toLocaleDateString("en-IN", { month: "short", year: "2-digit" })}</Typography>
                    </Box>
                  ))}
                </Box>
                {/* today */}
                <Box sx={{ position: "absolute", left: x(new Date()), top: 0, bottom: 0, borderLeft: "2px solid #F04438", zIndex: 2, pointerEvents: "none" }} />
                {data.epics.map((e) => {
                  const pv = preview?.id === String(e._id) ? preview : null;
                  const s = pv ? dayIso(pv.start) : e.barStart || e.barEnd;
                  const en = pv ? dayIso(pv.end) : e.barEnd || e.barStart;
                  return (
                    <Box key={e._id} sx={{ height: ROW, borderBottom: `1px solid ${T.border}`, position: "relative" }}>
                      {pv && (
                        <Typography sx={{ position: "absolute", top: 11, left: x(en) + pxPerDay + 6, fontSize: "0.62rem", fontWeight: 700, color: T.inkSecondary, whiteSpace: "nowrap", pointerEvents: "none", zIndex: 3 }}>
                          {formatDate(s)} → {formatDate(en)}
                        </Typography>
                      )}
                      {s ? (
                        <Tooltip title={`${e.key} · ${formatDate(s)} → ${formatDate(en)} · ${e.done}/${e.issues} done${canDrag ? " · drag to move, drag an edge to resize" : ""}`} disableHoverListener={Boolean(preview)}>
                          <Box ref={(el) => { barRefs.current[String(e._id)] = el; }} onClick={() => onBarClick(e)} onPointerDown={(ev) => startDrag(ev, e, "move")}
                            sx={{ position: "absolute", top: 8, height: ROW - 16, left: x(s), width: Math.max(10, x(en) - x(s) + pxPerDay), borderRadius: "6px", bgcolor: e.statusCategory === "done" ? "#12B76A" : "#7A5AF8", opacity: pv ? 0.85 : e.startDate || e.dueDate ? 1 : 0.55, cursor: canDrag ? (pv ? "grabbing" : "grab") : "pointer", overflow: "hidden", touchAction: canDrag ? "none" : "auto", userSelect: "none", boxShadow: pv ? "0 2px 8px rgba(16,24,40,.25)" : "none" }}>
                            <Box sx={{ height: "100%", width: `${e.progressPct}%`, bgcolor: "rgba(255,255,255,.25)" }} />
                            {canDrag && <Box sx={handleSx("left")} onPointerDown={(ev) => startDrag(ev, e, "start")} />}
                            {canDrag && <Box sx={handleSx("right")} onPointerDown={(ev) => startDrag(ev, e, "end")} />}
                          </Box>
                        </Tooltip>
                      ) : canManage ? (
                        <Button size="small" sx={{ position: "absolute", left: x(new Date()) + 6, top: 6, fontSize: TYPE.micro }} onClick={() => setDatesFor(e)}>Set dates</Button>
                      ) : null}
                    </Box>
                  );
                })}
                <Box sx={{ height: ROW, position: "relative" }}>
                  {data.milestones.filter((m) => m.dueDate).map((m) => (
                    <Tooltip key={m._id} title={`${m.name} · ${formatDate(m.dueDate)} · ${m.progressPct}% (${m.status})`}>
                      <Box onClick={() => canManage && setMsDialog(m)} sx={{ position: "absolute", left: x(m.dueDate) - 7, top: 10, cursor: canManage ? "pointer" : "default", color: m.status === "missed" ? "#F04438" : m.status === "done" ? "#12B76A" : "#F79009" }}><Flag size={16} fill="currentColor" /></Box>
                    </Tooltip>
                  ))}
                  {data.versions.filter((v) => v.releaseDate).map((v) => (
                    <Tooltip key={v._id} title={`Release ${v.name} · ${formatDate(v.releaseDate)} (${v.status})`}>
                      <Box sx={{ position: "absolute", left: x(v.releaseDate) - 7, top: 10, color: "#12B76A" }}><Tag size={16} /></Box>
                    </Tooltip>
                  ))}
                </Box>
                <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "visible" }}>
                  <defs><marker id="arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 z" fill="#F04438" /></marker></defs>
                  {lines.map((l, i) => <path key={i} d={`M${l.x1},${l.y1} C${l.x1 + 30},${l.y1} ${l.x2 - 30},${l.y2} ${l.x2},${l.y2}`} stroke="#F04438" strokeWidth="1.5" fill="none" markerEnd="url(#arrow)" opacity="0.7" />)}
                </svg>
              </Box>
            </Box>
          </Box>
        </GlassCard>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5, mt: 2.5 }}>
        <GlassCard>
          <CardHeader icon={Flag} title="Milestones" />
          <Box sx={{ p: 2 }}>
            {!data.milestones.length && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>No milestones.</Typography>}
            {data.milestones.map((m) => (
              <Box key={m._id} sx={{ py: 1, borderBottom: `1px solid ${T.border}`, cursor: canManage ? "pointer" : "default" }} onClick={() => canManage && setMsDialog(m)}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: TYPE.small, fontWeight: 700, flexGrow: 1 }}>{m.name}</Typography>
                  <Chip size="small" label={m.status} sx={{ height: 20, fontSize: "0.66rem" }} />
                  <Typography sx={{ fontSize: TYPE.micro, color: m.dueDate && new Date(m.dueDate) < new Date() && m.status === "open" ? "#B42318" : T.inkMuted }}>{m.dueDate ? formatDate(m.dueDate) : "No date"}</Typography>
                </Stack>
                <Progress value={m.progressPct} label={`${m.done}/${m.issues} issues`} />
              </Box>
            ))}
          </Box>
        </GlassCard>
        <GlassCard>
          <CardHeader icon={Link2} title="Cross-project dependencies" subtitle="Blocks / depends-on links with other projects" />
          <Box sx={{ p: 2 }}>
            {!data.projectDependencies.length && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>None.</Typography>}
            {data.projectDependencies.map((d, i) => (
              <Typography key={i} sx={{ fontSize: TYPE.small, py: 0.5 }}>
                <b style={{ cursor: "pointer" }} onClick={() => navigate(`/projects/${d.source.key.split("-")[0]}/issues/${d.source.key}`)}>{d.source.key}</b> {d.label} <b style={{ cursor: "pointer" }} onClick={() => navigate(`/projects/${d.target.key.split("-")[0]}/issues/${d.target.key}`)}>{d.target.key}</b>
                <span style={{ color: T.inkMuted }}> — {d.target.summary}</span>
                {d.source.statusCategory !== "done" && <Chip size="small" label="open blocker" sx={{ ml: 1, height: 18, fontSize: "0.62rem", bgcolor: "#FEE4E2", color: "#B42318" }} />}
              </Typography>
            ))}
          </Box>
        </GlassCard>
      </Box>
      <MilestoneDialog state={msDialog} project={project} epics={data.epics} versions={data.versions} onClose={() => setMsDialog(null)} onSaved={() => { setMsDialog(null); setTick((t) => t + 1); }} />
      <DatesDialog epic={datesFor} onClose={() => setDatesFor(null)} onSaved={() => { setDatesFor(null); setTick((t) => t + 1); }} />
    </Box>
  );
};

export default Roadmap;
